import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { WsUrlsService } from './wsurls.service';
import { WebServicesService } from './webservices.service';
import { DialogService } from './dialog.service';
import { DateParserService } from './dateparser.service';
import { UserProfile, Event, Entity, EventReservation, SubscriberView, Manifestation, ManifestationCategory } from '../_models/models';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable()
export class ApiService {

  constructor(
    private _router: Router,
    private _urls: WsUrlsService,
    private _ws: WebServicesService,
    private _dialog: DialogService,
    private _dateParser: DateParserService,
    private _domSanitizer: DomSanitizer,
  ) { }

  private getErrorFunction(): (error: any) => void {
    const dialog = this._dialog;
    const router = this._router;
    return (error) => {
      if (!error) {
        dialog.ws500alert();
      } else if (error.status === 401) {
        // expired token
        router.navigate(['/login/expired']);
      } else if (error.status === 400) {
        dialog.ws400alert(error.error);
      } else if (error.status === 403) {
        dialog.ws403alert();
      } else if (error.status === 404) {
        dialog.ws404alert();
      } else if (error.status === 500) {
        dialog.ws500alert();
      } else {
        dialog.ws500alert();
      }
    };
  }

  getMenu(): Observable<any> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getMenu());
  }

  deleteReservation(id: number): Observable<any> {
    return this._ws.deleteHttp(this.getErrorFunction(), this._urls.deleteReservation(id));
  }

  getEventAll(): Observable<Event[]> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getEventAll()).pipe(map((event: Event[]) => {
      event.map(e => {
        e.dateStart = this._dateParser.parseFromString('' + e.dateStart);
        e.dateEnd = this._dateParser.parseFromString('' + e.dateEnd);

        const eventStartHours = e.dateStart.getHours();
        const eventStartMinutes =  e.dateStart.getMinutes();
        const eventEndHours = e.dateEnd.getHours();
        const eventEndMinutes =  e.dateEnd.getMinutes();

        e._eventHours  = this._dateParser.getTimeString(eventStartHours, eventStartMinutes) + '-' + this._dateParser.getTimeString(eventEndHours, eventEndMinutes);

        e._reservationsLeft = e.totalSlots - e.reservedSlots;
        const availability = (e._reservationsLeft === 1) ? ' disponibile ' : ' disponibili';
        const slot = (e.totalSlots === 1) ? ' posto' : ' posti';
        e._reservationsString = e._reservationsLeft + availability + '/' + e.totalSlots + slot;

        const htmlProgress = '<div class="vias-progress-bar" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width:' + ((e._reservationsLeft) / e.totalSlots) * 100 + '%;' + '"></div>';
        const htmlAnyReservationsLeft = (e._reservationsLeft > 0) ? '<div class="vias-progress-bar-title">' + e._reservationsString + '</div>' : '<div class="bg-success vias-progress-bar-title">Posti esauriti!</div>';

        e._htmlPhotoUrl = '<div class="w-100 h-100"> <img class="img-fluid size-image" src="' + e.photoUrl + '" alt="No image"></div>';

        const str: string = '<div class="vias-progress mr-2">' + htmlProgress + htmlAnyReservationsLeft + '</div>';
        e._safeHtmlProgressBar = this._domSanitizer.bypassSecurityTrustHtml(str);

        return e;
      });
      return event;
    }));
  }

  postReservation(e: EventReservation): Observable<any> {
    return this._ws.postJson(this.getErrorFunction(), this._urls.postReservation(e.event.id), e);
  }

  getSubscriberAll(): Observable<SubscriberView[]> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getSubscriberAll()).pipe(map((subscribers: SubscriberView[]) => {
      subscribers.map(s => {
        const today = new Date();
        s.eventDateStart = this._dateParser.parseFromString('' + s.eventDateStart);
        s.eventDateEnd = this._dateParser.parseFromString('' + s.eventDateEnd);
        s._flgTerminated = new Date(s.eventDateEnd).getTime() < today.getTime();
        return s;
      });
      return subscribers;
    }));
  }

  getSubscriberByEmail(email: string): Observable<UserProfile> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getSubscriberByEmail(email)).pipe(map((userProfile: UserProfile) => {
      if (userProfile && userProfile.dateOfBirth) {
        userProfile.dateOfBirth = this._dateParser.parseFromString('' + userProfile.dateOfBirth);
      }
      return userProfile;
    }));
  }

  getEntityAll(showRemoved?: boolean): Observable<Entity[]> {
    return this._ws.postJson(this.getErrorFunction(), this._urls.getEntityAll(), showRemoved).pipe(map((entities: Entity[]) => {
      return entities.map(e => {
        e._canBeDeleted = !e.disable;
        e._canBeRestored = e.disable;
        return e;
      });
    }));
  }

  getUserProfile(requestUid: string) {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getUserProfile(requestUid));
  }

  getCheckUser(requestUid: string) {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getCheckUser(requestUid));
  }

  postRegisterUserProfile(isRegisteredByOperator: boolean, requestUid: string, profile: UserProfile): Observable<any> {
    return this._ws.postJson(this.getErrorFunction(), this._urls.postRegisterUserProfile(isRegisteredByOperator, requestUid), profile);
  }

  getEventDetail(eventId: number): Observable<Event> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getEventDetail(eventId)).pipe(map((eventDetail: Event[]) => {
      eventDetail.forEach(e => {
        e._reservationsLeft = e.totalSlots - e.reservedSlots;
        e._startDate =  this._dateParser.parseFromString('' + e.dateStart);
        e._startDateHour = this._dateParser.parseFromString('' + e.dateStart);
        e._endDate = this._dateParser.parseFromString('' + e.dateEnd);
        return e;
      });
      return eventDetail;
    }));
  }

  getEntityDetail(entityId: number) {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getEntityDetail(entityId));
  }

  putEvent(event: Event): Observable<any> {
    return this._ws.putJson(this.getErrorFunction(), this._urls.putEvent(event.id), event);
  }

  putEntity(entity: Entity): Observable<any> {
    return this._ws.putJson(this.getErrorFunction(), this._urls.putEntity(entity.id), entity);
  }

  postEvent(event: Event): Observable<any> {
    return this._ws.postJson(this.getErrorFunction(), this._urls.postEvent(), event);
  }

  deleteEvent(eventId: number): Observable<any> {
    return this._ws.deleteHttp(this.getErrorFunction(), this._urls.deleteEvent(eventId));
  }

  disableEntity(entity: Entity): Observable<any> {
    return this._ws.putJson(this.getErrorFunction(), this._urls.disableEntity(entity.id), entity);
  }

  postEntity(entity: Entity): Observable<any> {
    return this._ws.postJson(this.getErrorFunction(), this._urls.postEntity(), entity);
  }

  getManifestationAll(): Observable<any> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getManifestationAll()).pipe(map((manifestations: Manifestation[]) => {
      return manifestations.map(manifestation => {
        manifestation._htmlPhotoUrl = '<div class="w-100 h-100"> <img class="img-fluid size-image" src="' + manifestation.photoUrl + '" alt="No image"></div>';

        manifestation.dateStart = this._dateParser.parseFromString('' + manifestation.dateStart);
        manifestation.dateEnd = this._dateParser.parseFromString('' + manifestation.dateEnd);

        const manifestationStartHours = manifestation.dateStart.getHours();
        const manifestationStartMinutes =  manifestation.dateStart.getMinutes();
        const manifestationEndHours = manifestation.dateEnd.getHours();
        const manifestationEndMinutes =  manifestation.dateEnd.getMinutes();

        manifestation._startDate =  manifestation.dateStart;
        manifestation._viasStartDateHour = this._dateParser.getTimeString(manifestationStartHours, manifestationStartMinutes);
        manifestation._endDate = manifestation.dateEnd;
        manifestation._viasEndDateHour = this._dateParser.getTimeString(manifestationEndHours, manifestationEndMinutes);
        manifestation._reverseCategory = ManifestationCategory.matches[manifestation.category];
        return manifestation;
      });
    }));
  }

  getManifestationDetail(id: number): Observable<Manifestation> {
    return this._ws.getWithoutCache(this.getErrorFunction(), this._urls.getManifestationDetail(id)).pipe(map((manifestationsDetail: Manifestation[]) => {
      return manifestationsDetail.map(manifestationDetail => {
        manifestationDetail._startDate =  this._dateParser.parseFromString('' + manifestationDetail.dateStart);
        manifestationDetail._startDateHour = this._dateParser.parseFromString('' + manifestationDetail.dateStart);
        manifestationDetail._endDate = this._dateParser.parseFromString('' + manifestationDetail.dateEnd);
        manifestationDetail._endDateHour = this._dateParser.parseFromString('' + manifestationDetail.dateEnd);
        manifestationDetail._reverseCategory = ManifestationCategory.matches[manifestationDetail.category];
        return manifestationDetail;
      });
    }));
  }

  postManifestation(manifestation: Manifestation): Observable<Manifestation> {
    return this._ws.postJson(this.getErrorFunction(), this._urls.postManifestation(), manifestation);
  }

  putManifestation(manifestation: Manifestation): Observable<Manifestation> {
    return this._ws.putJson(this.getErrorFunction(), this._urls.putManifestation(manifestation.id), manifestation);
  }

  deleteManifestation(id: number): Observable<Manifestation> {
    return this._ws.deleteHttp(this.getErrorFunction(), this._urls.deleteManifestation(id));
  }

}
